<template>
  <div
    ref="anchorRef"
    class="tm-custom-tooltip__anchor"
  >
    <slot name="anchor" />
  </div>
  <transition
    appear
    name="q-transition--fade"
    @after-leave="emit('close-animation-end')"
  >
    <teleport
      v-if="isTooltipOpen"
      to="body"
    >
      <div
        ref="tooltipRef"
        class="tm-custom-tooltip__tooltip-wrapper"
        :class="{
          'tm-custom-tooltip__tooltip-wrapper--content-hidden': !isTooltipContentVisible,
          'tm-custom-tooltip__tooltip-wrapper--interactive': interactive,
        }"
        :style="[positionStyle, delay > 0 ? { '--tm-tooltip-delay': `${delay}ms` } : null]"
      >
        <tm-angle-wrap-tooltip
          class="tm-custom-tooltip__tooltip"
          :color="color"
          :offset="offset"
          :direction="position.direction"
          :custom="custom"
          :viewport-width="position.viewportWidth"
          :viewport-height="position.viewportHeight"
          :anchor-bounding="position.anchorBoundingRect"
          :tooltip-position="position.tooltipPosition"
        >
          <div ref="tooltipContentRef">
            <slot />
          </div>
        </tm-angle-wrap-tooltip>
      </div>
    </teleport>
  </transition>
</template>

<script setup lang="ts">
/**
 * @example
 * Simple text tooltip:
 * <div v-tooltip="{ text: 'Hello' }">...</div> // See TmTextTooltipDirectiveData type for more options
 *
 * @example
 * Simple translated tooltip:
 * <div v-tooltip="{ variant: 'translate', keypath: 'common.hello' }">...</div> // See TmTranslateTooltipDirectiveData type for more options
 *
 * @example
 * Custom tooltip with anchor provided via props:
 * <tm-custom-tooltip :anchor-element="anchorElement">Tooltip body</tm-custom-tooltip>
 *
 * @example
 * Custom tooltip with anchor provided via slots:
 * <tm-custom-tooltip>
 *   <template #anchor>
 *     <div>Anchor element</div>
 *   </template>
 *   Tooltip body
 * </tm-custom-tooltip>
 *
 * @example
 * Custom tooltip with parent element as anchor:
 * <div>
 *   Anchor
 *   <tm-custom-tooltip>
 *     Tooltip body
 *   </tm-custom-tooltip>
 * </div>
 */
import {
  tooltipDefaultColor,
  tooltipDefaultDirection,
  type ElementBoundingRect,
  type TmBaseTooltipPropsType,
  type TooltipDirection,
} from '@/components/shared/tooltip/types'
import TmAngleWrapTooltip from '@/components/shared/tooltip/TmAngleWrapTooltip.vue'
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  useSlots,
  useTemplateRef,
  watch,
} from '@/composition/vue/compositionApi'
import {
  getScrollbarWraps,
  getTooltipPositionStyle,
  isElementInsideContainer,
} from '@/composition/tooltip/positionEngineTooltip'
import { addPxToNumber } from '@/utils/string/addPxToNumber'
import { useTooltipPositionUpdating } from '@/composition/tooltip/useTooltipPositionUpdating'
import { rafThrottle } from '@/utils/rafThrottle'

const {
  direction = tooltipDefaultDirection,
  color = tooltipDefaultColor,
  interactive = false,
  disabled = false,
  delay = 0,
  // If persistence is provided, the tooltip will be opened or closed depending on the isOpen value.
  persistence,
  ...props
} = defineProps<TmBaseTooltipPropsType>()

const emit = defineEmits<{
  close: []
  'close-animation-end': []
}>()

const { subscribeToUpdateTooltipPosition, unsubscribeFromUpdateTooltipPosition } = useTooltipPositionUpdating()
let updateTooltipPositionSubscriptionKey = ''

const isTooltipOpen = ref(false)
const isTooltipContentVisible = ref(false)

const anchorRef = useTemplateRef('anchorRef')
const tooltipRef = useTemplateRef('tooltipRef')
const tooltipContentRef = useTemplateRef('tooltipContentRef')

const closestScrolls = ref<HTMLElement[]>([])

const position = ref<{
  anchorBoundingRect?: ElementBoundingRect
  viewportWidth?: number
  viewportHeight?: number
  tooltipPosition?: { top?: number; right?: number; bottom?: number; left?: number; height: number }
  direction?: TooltipDirection
}>({})

const positionStyle = ref<
  | {
      top?: string
      right?: string
      bottom?: string
      left?: string
      height?: string
    }
  | undefined
>(undefined)

const slots = useSlots()

const anchor = computed(() => {
  // If the anchor element is provided via props, use it
  if (props.anchorElement) return props.anchorElement
  // If the anchor element is provided via slots, use it
  if (slots.anchor) return anchorRef.value
  // If the anchor element is not provided, use the parent element of the component
  if (anchorRef.value) return anchorRef.value.parentElement
  return null
})

const updateTooltipPositionImmediately = async () => {
  positionStyle.value = undefined // prevents content from being compressed when there is still space on the left side
  await nextTick()
  if (!anchor.value || !tooltipRef.value) return

  const { newDirection, top, right, bottom, left, height, anchorBoundingRect, viewportWidth, viewportHeight } =
    getTooltipPositionStyle(direction, anchor.value, tooltipRef.value, props.offset)

  positionStyle.value = {
    top: (typeof top === 'number' && addPxToNumber(top)) || undefined,
    right: (typeof right === 'number' && addPxToNumber(right)) || undefined,
    bottom: (typeof bottom === 'number' && addPxToNumber(bottom)) || undefined,
    left: (typeof left === 'number' && addPxToNumber(left)) || undefined,
    height: (typeof height === 'number' && addPxToNumber(height)) || undefined,
  }
  // If the tooltip is out of viewport, hide it
  if (
    !persistence &&
    (anchorBoundingRect.top + anchorBoundingRect.height < 0 || // top
      anchorBoundingRect.left > viewportWidth || // right
      anchorBoundingRect.top > viewportHeight || // bottom
      anchorBoundingRect.right < 0) // left
  ) {
    hideTooltip()
    return
  }
  position.value = {
    anchorBoundingRect,
    viewportWidth,
    viewportHeight,
    tooltipPosition: { top, right, bottom, left, height },
    direction: newDirection,
  }
}

const updateTooltipPosition = rafThrottle(updateTooltipPositionImmediately)

const shouldHideTooltip = (e?: MouseEvent): boolean => {
  return (
    !persistence &&
    !(
      e?.relatedTarget &&
      (isElementInsideContainer(e.relatedTarget as Node, tooltipRef.value) ||
        isElementInsideContainer(e.relatedTarget as Node, anchor.value))
    )
  )
}

const handleMouseLeave = (e?: MouseEvent) => {
  if (shouldHideTooltip(e)) {
    hideTooltip()
  }
}

const tooltipContentResizeObserver = new ResizeObserver(() => {
  updateTooltipPosition()
})

const showTooltip = async () => {
  if (disabled) return
  if (isTooltipOpen.value) return

  isTooltipOpen.value = true
  await updateTooltipPositionImmediately()

  anchor.value?.addEventListener('mouseleave', handleMouseLeave)
  if (interactive) {
    tooltipRef.value?.addEventListener('mouseleave', handleMouseLeave)
  }
  window.addEventListener('resize', updateTooltipPosition)

  closestScrolls.value = getScrollbarWraps(anchor.value)
  if (closestScrolls.value.length) {
    closestScrolls.value.forEach((scrollEl) => {
      scrollEl.addEventListener('scroll', updateTooltipPosition)
    })
  }

  if (tooltipContentRef.value) {
    tooltipContentResizeObserver.observe(tooltipContentRef.value)
  }

  updateTooltipPositionSubscriptionKey = subscribeToUpdateTooltipPosition(updateTooltipPosition)

  isTooltipContentVisible.value = true
}

const onMouseEnterHandler = () => {
  if (persistence) return
  showTooltip()
}

const removeListeners = () => {
  updateTooltipPosition.cancel()
  anchor.value?.removeEventListener('mouseleave', handleMouseLeave)
  tooltipRef.value?.removeEventListener('mouseleave', handleMouseLeave)
  window.removeEventListener('resize', updateTooltipPosition)
  closestScrolls.value.forEach((scrollEl) => {
    scrollEl.removeEventListener('scroll', updateTooltipPosition)
  })
  if (tooltipContentRef.value) {
    tooltipContentResizeObserver.disconnect()
  }
  unsubscribeFromUpdateTooltipPosition(updateTooltipPositionSubscriptionKey)
}

const hideTooltip = () => {
  removeListeners()
  isTooltipOpen.value = false
  isTooltipContentVisible.value = false
  emit('close')
}

watch(
  () => persistence,
  (newPersistence) => {
    if (!newPersistence) return
    if (newPersistence.isOpen) {
      showTooltip()
    } else {
      hideTooltip()
    }
  },
  {
    immediate: true,
  },
)

onMounted(() => {
  anchor.value?.addEventListener('mouseenter', onMouseEnterHandler)
  // Lazy-mounted tooltips (via v-tooltip directive) are created on first mouseenter.
  // The mouseenter that would have triggered showTooltip has already fired by the time
  // we mount, so explicitly open if the cursor is still over the anchor.
  if (!persistence && !disabled && anchor.value?.matches(':hover')) {
    showTooltip()
  }
})

onBeforeUnmount(() => {
  anchor.value?.removeEventListener('mouseenter', onMouseEnterHandler)
  removeListeners()
})
</script>

<style lang="scss" scoped>
.tm-custom-tooltip {
  &__tooltip-wrapper {
    position: fixed;
    z-index: 7000; // q-menu: 6000
    display: flex;
    flex-direction: column;

    &:not(&--interactive) {
      pointer-events: none !important;
    }
    &--content-hidden {
      visibility: hidden;
    }
  }

  &__tooltip {
    max-height: 100%;
  }
}

.q-transition--fade-enter-active {
  transition-delay: var(--tm-tooltip-delay, 0); /* direct v-bind doesn't work here due to Teleport */
}
</style>
